<template>
  <div class="grid-x margin-top-xs pickup-info">
    <Radio
      class="pickup-radio-button"
      :name="page === 'bag' ? `fulfillment-${productId}-bag-${bagItemId}` : `fulfillment-${productId}`"
      :options="options"
      spacing="margin-bottom-z"
      v-bind="$attrs"
    />
  </div>
  <div
    v-if="labelText"
    class="radio-label pickup-radio-label"
    :class="{disabled: options[0].disabled}"
  >
    <div class="margin-left-m pickup-availability">
      {{ labelText }}
    </div>
  </div>
  <div
    v-if="badgingMessage && !options[0].disabled"
    class="event-badging margin-left-m"
    :style="{color: badgingColor}"
  >
    {{ badgingMessage }}
  </div>

  <Slideout
    v-if="locatorSlideoutShown"
    :id="`locator-slideout-${productId}`"
    :title="`Find a store near you`"
    @vue:mounted="slideoutMounted"
    @close-slideout="handleSlideoutClose"
  >
    <Locator
      :type-of="typeOf"
      :division-number="divisionNumber"
      :location-radius="100"
      :my-store-id="myStoreId"
      :upc-number="upcNumber"
      :results-displayed="100"
      :zip-code="zipCode"
      :use-fcc-api="true"
      :bag-items="bagItems"
      :page="page"
      :product-id="productId"
      :search-location-by-address="false"
      :fcc-macys-api-key="fccMacysApiKey"
      :show-accordion="showAccordion"
      @storeSelected="handleStoreSelected"
      @locationError="handleLocationError"
    />
    <template #footer>
      <div
        v-if="showLocatorContinue"
        class="grid-x grid-margin-x"
      >
        <button
          class="cell button small-12 primary"
          @click="handleLocatorContinue"
        >
          Continue
        </button>
      </div>
    </template>
  </Slideout>
</template>

<script>
import {
  h, ref, watch, computed, defineAsyncComponent, nextTick,
} from 'vue';
import { useStore } from 'vuex';
import Radio from '@atomic-ui/radio';
import Experimentation from '@common-vue/util/src/modules/experimentation/Experimentation';
import { brand } from 'config';
import generateBopsMessage from '../../utils/generateBopsMessage';
import generateBossMessage from '../../utils/generateBossMessage';
import getPickupAvailability from '../../utils/getPickupAvailability';
import PickupRadioLabelText from './PickupRadioLabelText';
import getBadgingDetails from '../../utils/getBadgingDetails';

export default {
  name: 'Pickup',
  components: {
    Radio,
    Slideout: defineAsyncComponent(() => import(/* webpackChunkName: "StoreLocatorSlideout", webpackPrefetch: true */'@atomic-ui/slideout/Slideout')),
    Locator: defineAsyncComponent(() => import(/* webpackChunkName: "StoreLocatorSlideout", webpackPrefetch: true */'../Locator')),
  },
  inheritAttrs: false,
  props: {
    storeDetails: {
      type: Object,
      required: true,
    },
    productAvailabilityInfo: {
      type: Object,
      required: true,
    },
    productId: {
      type: String,
      required: true,
    },
    bagItemId: {
      type: String,
      required: true,
    },
    bossEnabled: {
      type: Boolean,
    },
    page: {
      type: String,
      default: '',
    },
    bagItems: {
      type: Array,
      default: () => [],
    },
    sddEligibilityFlag: {
      type: Boolean,
    },
    showAccordion: {
      type: Boolean,
    },
    fccMacysApiKey: {
      type: String,
      default: '',
    },
    upcNumber: {
      type: String,
      default: '',
    },
    toggleLoaderForSlideout: {
      type: Function,
    },
    pickupEventData: {
      type: Object,
      default: null,
    },
    deliveryDays: {
      type: Object,
      default: null,
    },
    productCategoryId: {
      type: String,
      required: true,
    },
  },
  emits: ['option-change', 'update-radio-button', 'updated:storeDetails'],
  setup(props, { emit }) {
    const options = ref([]);
    const store = useStore();
    const divisionNumber = computed(() => (process.env.ENTRY_POINT.toUpperCase() === 'MCOM' ? 12 : 13));
    const typeOf = computed(() => (divisionNumber.value === 12 ? 'MSTORE' : 'BSTORE'));
    const myStoreId = ref(null);
    const myStoreName = ref(null);
    const zipCode = ref(null);
    const locatorSlideoutShown = ref(false);
    const showLocatorContinue = ref(false);
    const disabledPickupShowStore = ref(true);
    const labelText = ref('');
    const id = (props.page === 'bag') ? `pick-up-${props.productId}-bag-${props.bagItemId}` : `pick-up-${props.productId}`;
    const ariaLabel = ref(`Change Store ${ props.storeDetails?.name}`);
    const previouslyFocusedElement = ref(null);
    const isExperimentForPickupCopyClarity = ref(false);
    const badgingMessage = ref('');
    const badgingColor = ref('');

    Experimentation.isRecipeSelected('2120-21').then((flag) => {
      isExperimentForPickupCopyClarity.value = flag;
    });

    const openSlideout = () => {
      store.commit('slideoutStore/TOGGLE', `locator-slideout-${props.productId}`);
    };

    const slideoutMounted = () => {
      emit('toggleLoaderForSlideout', false);
      requestAnimationFrame(openSlideout);
    };

    const handleChangeLocation = async () => {
      emit('toggleLoaderForSlideout', true);
      locatorSlideoutShown.value = true;
      previouslyFocusedElement.value = document.activeElement;
      await nextTick();
    };

    const updateOptions = () => {
      if (!props.storeDetails?.name) {
        labelText.value = '';
        options.value = [{
          label: h(PickupRadioLabelText, {
            message: 'Pick up at store',
            noStore: true,
            storeName: props.storeDetails?.name,
            disabledPickupShowStore: disabledPickupShowStore.value,
            ariaLabel: ariaLabel.value,
            onChangeLocation: handleChangeLocation,
          }),
          id,
          value: 'Pickup',
          ariaLabel: 'Pick up at store. Check your local store if you want it sooner.',
          labelType: 'html',
          disabled: true,
        }];
      } else if (props.productAvailabilityInfo) {
        const { bossAvailabilityMessage, marketplaceItemFlag } = props.productAvailabilityInfo;
        const { isBossUpc, isAvailableForPickup } = getPickupAvailability(props.productAvailabilityInfo, props.bossEnabled);
        if (!isAvailableForPickup) {
          disabledPickupShowStore.value = !marketplaceItemFlag;
          emit('option-change'); // emit to change the selected radio button when pickup is unavailable
          const notAvailableLabel = marketplaceItemFlag ? 'Not available for pickup' : 'Not available for pickup at ';
          const label = isAvailableForPickup ? 'Pickup at ' : notAvailableLabel;
          labelText.value = '';
          options.value = [{
            label: h(PickupRadioLabelText, {
              message: label,
              noStore: true,
              storeName: props.storeDetails?.name,
              disabledPickupShowStore: disabledPickupShowStore.value,
              ariaLabel: ariaLabel.value,
              onChangeLocation: handleChangeLocation,
            }),
            id,
            value: 'Pickup',
            ariaLabel: `${label}`,
            ariaDescription: 'Not available for pickup at this store',
            labelType: 'html',
            disabled: true,
          }];
        } else {
          const {
            topLineMsg, pickupLabel, value, disabled,
          } = isBossUpc
            ? generateBossMessage({ ...props.storeDetails, bossAvailabilityMessage }, isExperimentForPickupCopyClarity.value) // BOSS MSG
            : generateBopsMessage(props.storeDetails, isExperimentForPickupCopyClarity.value); // BOPS MSG
          labelText.value = pickupLabel;

          if (props.pickupEventData) {
            const eventData = getBadgingDetails(props.pickupEventData, props.deliveryDays, isBossUpc, props.productCategoryId, topLineMsg);

            if (eventData) {
              if (isBossUpc) {
                badgingColor.value = eventData.color;
                badgingMessage.value = eventData.message;
              } else if (brand === 'mcom') {
                badgingColor.value = eventData.color;
                badgingMessage.value = eventData.message;
              } else {
                badgingColor.value = eventData.color;
              }
            } else {
              badgingColor.value = '';
              badgingMessage.value = '';
            }
          }
          options.value = [{
            label: h(PickupRadioLabelText, {
              message: topLineMsg,
              storeName: props.storeDetails?.name,
              disabledPickupShowStore: disabledPickupShowStore.value,
              ariaLabel: ariaLabel.value,
              onChangeLocation: handleChangeLocation,
              badgingColor: brand === 'mcom' ? '' : badgingColor.value,
            }),
            id,
            value,
            ariaLabel: `${topLineMsg} at store ${props.storeDetails?.name}`,
            ariaDescription: ` ${pickupLabel}`,
            labelType: 'html',
            disabled,
          }];
        }
      } else {
        labelText.value = 'Select a size and/or color to view shipping and in-store availability.';
        options.value = [{
          label: h(PickupRadioLabelText, {
            message: 'Pick up at store',
            storeName: props.storeDetails?.name,
            disabledPickupShowStore: disabledPickupShowStore.value,
            ariaLabel: ariaLabel.value,
            onChangeLocation: handleChangeLocation,
          }),
          id,
          value: 'Pickup',
          ariaLabel: 'Pick up at store. Select a size and/or color to view shipping and in-store availability.',
          labelType: 'html',
          disabled: true,
        }];
      }

      myStoreId.value = props.storeDetails?.storeId;
      myStoreName.value = props.storeDetails?.name;
      zipCode.value = props.storeDetails?.zipCode?.split('-')[0];
    };

    watch([() => props.productAvailabilityInfo, isExperimentForPickupCopyClarity], updateOptions, { immediate: true });

    watch(() => props.pickupEventData, () => {
      updateOptions();
    });

    const handleStoreSelected = (storeVal) => {
      emit('updated:storeDetails', {
        currentStoreDetails: {
          myStoreId: storeVal.locationNumber,
          myStoreName: storeVal.name,
        },
      });
      ariaLabel.value = `The new store is ${ storeVal.name }. Change Store Button`;
      store.commit('slideoutStore/TOGGLE', `locator-slideout-${props.productId}`);
      if (props.page === 'pdp') {
        emit('update-radio-button');
      }
    };

    const handleLocationError = (val) => {
      if (val.status === 404) {
        showLocatorContinue.value = true;
      }
    };

    const handleSlideoutClose = async () => {
      locatorSlideoutShown.value = false;
      await nextTick();
      previouslyFocusedElement?.value?.focus();
    };

    const handleLocatorContinue = () => {
      store.commit('slideoutStore/TOGGLE', `locator-slideout-${props.productId}`);
    };

    return {
      options,
      typeOf,
      divisionNumber,
      myStoreId,
      myStoreName,
      zipCode,
      labelText,
      ariaLabel,
      showLocatorContinue,
      locatorSlideoutShown,
      handleLocatorContinue,
      handleStoreSelected,
      handleChangeLocation,
      handleLocationError,
      handleSlideoutClose,
      openSlideout,
      disabledPickupShowStore,
      isExperimentForPickupCopyClarity,
      slideoutMounted,
      badgingMessage,
      badgingColor,
      getBadgingDetails,
    };
  },
};
</script>

<style lang="scss" scoped>
.radio-label.disabled{
    color: $gray-2-color;
  }

.pickup-radio-button {
  position: relative;
  z-index: 2;
  margin-right: rem-calc(5);
}
.event-badging {
  font-weight:bold;
}

@media screen and (max-width: 365px) {
  .store-button {
    margin-left: 2rem;
  }
}

</style>
